import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  createBackup, getBackupHistory, downloadBackup, deleteBackup,
  getBackupConfig, updateBackupConfig, downloadExcelBackup, testScheduledBackup 
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Database, Download, Trash2, Clock, HardDrive, RefreshCw, Calendar, FileSpreadsheet, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const BackupPage = () => {
  const { isAdmin } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState(null);
  
  const [config, setConfig] = useState({
    enabled: false,
    frequency: 'daily',
    day_of_week: 0,
    time: '00:00'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [backupsRes, configRes] = await Promise.all([
        getBackupHistory(),
        getBackupConfig()
      ]);
      setBackups(backupsRes.data);
      if (configRes.data) {
        setConfig(configRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch backup data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      await createBackup();
      toast.success('Backup created successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (backup) => {
    try {
      const response = await downloadBackup(backup.id);
      const isExcel = backup.filename.endsWith('.xlsx');
      const mimeType = isExcel ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/json';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backup.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download backup');
    }
  };

  const handleDelete = async () => {
    if (!backupToDelete) return;
    try {
      await deleteBackup(backupToDelete.id);
      toast.success('Backup deleted');
      setDeleteDialogOpen(false);
      setBackupToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const handleConfigUpdate = async () => {
    try {
      await updateBackupConfig(config);
      toast.success('Backup configuration updated');
    } catch (error) {
      toast.error('Failed to update configuration');
    }
  };

  const [excelLoading, setExcelLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const handleTestScheduledBackup = async () => {
    try {
      setTestingEmail(true);
      await testScheduledBackup();
      toast.success('Scheduled backup email sent to all Super Admins');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally { setTestingEmail(false); }
  };

  const handleExcelBackup = async () => {
    try {
      setExcelLoading(true);
      const response = await downloadExcelBackup();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `UniWork_Full_Backup_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Excel backup downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download Excel backup');
    } finally { setExcelLoading(false); }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">You don't have permission to access this page</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Backup & Recovery</h1>
          <p className="text-muted-foreground mt-1">
            Manage database backups and recovery options
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleExcelBackup}
            disabled={excelLoading}
            variant="outline"
            className="font-semibold"
            data-testid="excel-backup-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {excelLoading ? 'Generating...' : 'Download Excel Backup'}
          </Button>
          <Button 
            onClick={handleCreateBackup}
            disabled={creating}
            className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold"
            data-testid="create-backup-btn"
          >
            <Database className="h-4 w-4 mr-2" />
            {creating ? 'Creating...' : 'Create & Save Backup'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Backup History */}
        <div className="lg:col-span-2">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />
                Backup History
              </CardTitle>
              <CardDescription>View and manage your backup files</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
                </div>
              ) : backups.length === 0 ? (
                <div className="text-center py-8">
                  <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No backups found</p>
                  <p className="text-sm text-muted-foreground mt-1">Create your first backup</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {backups.map((backup) => (
                    <div 
                      key={backup.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-[#F5A623]/50 transition-colors"
                      data-testid={`backup-item-${backup.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-[#F5A623]/10 flex items-center justify-center">
                          <Database className="h-5 w-5 text-[#F5A623]" />
                        </div>
                        <div>
                          <p className="font-medium">{backup.filename}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <span>{formatFileSize(backup.size)}</span>
                            <span>•</span>
                            <span>{new Date(backup.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={backup.backup_type === 'manual' ? 'badge-info' : 'badge-success'}>
                          {backup.backup_type}
                        </Badge>
                        <Button variant="ghost" size="icon" onClick={() => handleDownload(backup)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => { setBackupToDelete(backup); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Scheduled Backup Config */}
        <div>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Scheduled Backup
              </CardTitle>
              <CardDescription>Automated Excel backups emailed to Super Admins via SendGrid</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <Label htmlFor="enabled" className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Enable Scheduled Backups
                </Label>
                <Switch
                  id="enabled"
                  checked={config.enabled}
                  onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
                  data-testid="enable-scheduled-switch"
                />
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select 
                    value={config.frequency} 
                    onValueChange={(v) => setConfig({ ...config, frequency: v })}
                    disabled={!config.enabled}
                  >
                    <SelectTrigger data-testid="frequency-select">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {config.frequency === 'weekly' && (
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select 
                      value={config.day_of_week?.toString()} 
                      onValueChange={(v) => setConfig({ ...config, day_of_week: parseInt(v) })}
                      disabled={!config.enabled}
                    >
                      <SelectTrigger data-testid="day-select">
                        <SelectValue placeholder="Select day" />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map((day, idx) => (
                          <SelectItem key={day} value={idx.toString()}>{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="time">Time (24-hour format)</Label>
                  <Input
                    id="time"
                    type="time"
                    value={config.time}
                    onChange={(e) => setConfig({ ...config, time: e.target.value })}
                    disabled={!config.enabled}
                    data-testid="time-input"
                  />
                </div>
              </div>

              <Button 
                onClick={handleConfigUpdate}
                className="w-full bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold"
                data-testid="save-config-btn"
              >
                Save Configuration
              </Button>
              <Button 
                onClick={handleTestScheduledBackup}
                disabled={testingEmail}
                variant="outline"
                className="w-full mt-2"
                data-testid="test-email-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                {testingEmail ? 'Sending...' : 'Send Test Backup Email'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Backup</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{backupToDelete?.filename}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
